import { memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckIcon, StarIcon, PlusIcon } from 'lucide-react';
import { useCartActions } from '../../contexts/StoreContext';
import { useFlash } from '../../hooks/useFlash';
import { ProductImage } from '../../ui/ProductImage';
import { productMeta } from '../../utils/catalog';
import { shopLabels } from '../../data/shopLabels';
import type { Product } from '../../core/types/catalog';
import { euro, rating } from '../../core/format/currency';
import { isLowStock, isSoldOut } from '../../core/rules/stock';
import { activePromo } from '../../core/rules/promo';
import { btnAddRound } from '../../ui/styles';

/**
 * Productkaart voor grids. Gememoriseerd en alleen gekoppeld aan de (stabiele)
 * mand-acties: iets toevoegen tekent de hele productgrid dus niet opnieuw.
 */
type Props = {
  product: Product;
  /**
   * Kopniveau van de productnaam, aansluitend op de pagina: 2 direct onder de h1 (de collectie),
   * 3 onder een sectiekop (home, over, "past goed bij"). Nooit een niveau overslaan.
   */
  headingLevel?: 2 | 3;
};
export const ProductCard = memo(function ProductCard({
  product,
  headingLevel = 3
}: Props) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const {
    add
  } = useCartActions();
  // Bevestiging in de knop zelf: het oog blijft bij het product, de badge in de header wipt mee.
  const [added, flashAdded] = useFlash();
  const onAdd = () => {
    if (add(product)) flashAdded();
  };
  const soldOut = isSoldOut(product);
  const lowStock = isLowStock(product);
  const meta = productMeta(product);
  const promo = activePromo(product);
  const path = `/webshop/${product.id}`;
  // Vanuit de collectie: onthoud de filters, zodat "← Webshop" op de productpagina daar terugkomt.
  const {
    pathname,
    search
  } = useLocation();
  const linkState = pathname === '/webshop' ? {
    fromShop: `${pathname}${search}`
  } : undefined;
  const badge = product.salonFavourite ? {
    label: 'Salonfavoriet',
    className: 'bg-clay text-shell'
  } : product.bestseller ? {
    label: 'Bestseller',
    className: 'bg-shell text-ink'
  } : null;
  return <article className="group flex h-full flex-col" data-id="element-8736" data-mp="269ze4es5x7p2">
      <div className="relative overflow-hidden bg-blush" data-id="element-8737" data-mp="268ekcrdo9ss9">
        {/* Dubbele link naar dezelfde pagina: uit de tabvolgorde en voor schermlezers verborgen. */}
        <Link to={path} state={linkState} className="block w-full" tabIndex={-1} aria-hidden="true" data-id="element-8738" data-mp="2fz98606kh0ft">
          <ProductImage src={product.image} alt={shopLabels.productImage(product)} className={`aspect-[4/5] w-full p-[7%] transition-transform duration-300 ease-soft group-hover:scale-[1.03] ${soldOut ? 'opacity-60' : ''}`} data-id="element-8739" data-mp="1ie3p5oownczf" />
        </Link>

        {soldOut ? <span className="absolute left-3 top-3 rounded-full bg-shell px-3 py-1 text-[12px] text-ink" data-id="element-8740" data-mp="1we4qt6czuofy">{shopLabels.soldOutShort}</span> : badge && <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[12px] ${badge.className}`} data-id="element-8741" data-mp="37r767y3cx7yj">{badge.label}</span>}

        {/* Snel toevoegen — alleen op brede schermen mét muis (hover én toetsenbordfocus).
            Een iPad in liggende stand is breed maar heeft geen hover: die krijgt de ronde knop onder de kaart. */}
        {!soldOut && <button type="button" onClick={onAdd} aria-label={shopLabels.addToCart(product)} className={`absolute inset-x-3 bottom-3 hidden items-center justify-center gap-2 rounded-full py-3 text-[14px] backdrop-blur-sm transition-[opacity,transform,background-color,color] duration-200 ease-soft focus-visible:translate-y-0 focus-visible:opacity-100 group-hover:translate-y-0 group-hover:opacity-100 [@media(hover:hover)_and_(min-width:1024px)]:flex ${added ? 'translate-y-0 bg-ink text-shell opacity-100' : 'translate-y-2 bg-shell/95 text-ink opacity-0 hover:bg-ink hover:text-shell'}`} data-id="element-8742" data-mp="n9qf5xop189o">
            {added ? <>
                <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8743" data-mp="qhhlyib0t8p7" />
                Toegevoegd
              </> : <>
                <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8744" data-mp="ve87vm9iayij" />
                In winkelmand · {euro(product.price)}
              </>}
          </button>}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3" data-id="element-8745" data-mp="mhh7ixnx8xi5">
        <p className="truncate text-[13px] text-clay" data-id="element-8746" data-mp="7p6z1xwrhlcq">{product.brand}</p>
        {product.reviews > 0 ? <p className="flex shrink-0 items-center gap-1 text-[13px] text-muted" data-id="element-8747" data-mp="cirnozcgt2z9">
            <span className="sr-only" data-id="element-8748" data-mp="3uwr63vslrkl6">{shopLabels.rating(product)}</span>
            <StarIcon className="h-3 w-3 fill-current text-clay" strokeWidth={0} aria-hidden="true" data-id="element-8749" data-mp="2qvo1juqd4sx" />
            <span aria-hidden="true" data-id="element-8750" data-mp="3kx08hl8qwpgv">
              {rating(product.rating)} <span className="text-muted" data-id="element-8751" data-mp="3pwd5gytbzcx6">({product.reviews})</span>
            </span>
          </p> : product.step && <p className="shrink-0 text-[13px] text-muted" data-id="element-8752" data-mp="369rru4wo3rxi">{product.step}</p>}
      </div>

      <Heading className="mt-1 font-display text-[1.2rem] leading-snug text-ink" data-id="element-8753" data-mp="31c03tlz58pyl">
        {/* Blok met binnenruimte: de hele titelregel is aan te tikken (≥ 44px), de opmaak schuift niet. */}
        <Link to={path} state={linkState} className="-my-2.5 block py-2.5 transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8754" data-mp="3b94pzlszolfe">
          {product.name}
        </Link>
      </Heading>
      {product.note && <p className="mt-1.5 line-clamp-2 text-[14px] leading-relaxed text-muted" data-id="element-8755" data-mp="3kavqmc8oa22e">{product.note}</p>}

      {(promo || lowStock) && <div className="mt-3 space-y-1.5" data-id="element-8756" data-mp="2tb8p4qu35xa">
          {promo && <p className="w-fit rounded-full bg-blush px-3 py-1 text-[13px] leading-snug text-ink" data-id="element-8757" data-mp="3u0fa9k47lzyb">{promo.label}</p>}
          {lowStock && <p className="text-[13px] text-clay" data-id="element-8758" data-mp="bvkgvnymqn7i">Nog {product.stock} op voorraad</p>}
        </div>}

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-line pt-3.5" data-id="element-8759" data-mp="7u8o59kgllv3">
        <p className="text-[13px] text-muted" data-id="element-8760" data-mp="exgk1v4gd5yh">
          <span className="font-display text-lg tabular-nums text-ink" data-id="element-8761" data-mp="jv8cqrcx8w3q">{euro(product.price)}</span>
          {meta && <span className="ml-2 hidden xl:inline" data-id="element-8762" data-mp="oulba9d5nrm5">{meta}</span>}
        </p>
        <button type="button" onClick={onAdd} disabled={soldOut} aria-label={soldOut ? shopLabels.soldOut(product) : shopLabels.addToCart(product)} className={`${btnAddRound(added)} [@media(hover:hover)_and_(min-width:1024px)]:hidden`} data-id="element-8763" data-mp="os8lxb52iez2">
          {added ? <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-8764" data-mp="17kt6846pbbr9" /> : <PlusIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8765" data-mp="1cjw5t93wwpru" />}
        </button>
      </div>
      {/* Het vinkje is alleen te zien; een schermlezer hoort het hier. */}
      <span className="sr-only" role="status" data-id="element-8766" data-mp="3eb97lpal2hjo">
        {added ? `${product.name} ${shopLabels.addedToCart}` : ''}
      </span>
    </article>;
});